import * as React from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ConfirmationModal } from './ConfirmationModal';
import { OneTimeOrderModal } from './OneTimeOrderModal';
import { Table } from './Table';
import { axios } from '../api';
import { InlineSpinner } from '../assets/Spinner';
import { isAllModuleAccessUser } from '../pages/constants';
import { AccountStatus, Customer, OrderStatus } from '../pages/types';
import { CustomerOrdersHeader } from '../tableHeaders';
import { BOTTOM_TOAST, formatAddress, generateErrorMsg, mapSntApiDate } from '../utils';

interface CustomerOrdersProps {
  customer: Customer;
  refreshCustomer: (onComplete?: () => void) => void;
}

const mapOrders = (orders: any) => {
  return orders
    .sort((a: any, b: any) => {
      const dateA: any = new Date(a.scheduled ? a.scheduled : a.charged);
      const dateB: any = new Date(b.scheduled ? b.scheduled : b.charged);

      return dateB - dateA;
    })
    .map((order: any) => {
      let fullAddress = '--';
      const orderDate = order.scheduled ? order.scheduled : order.charged;
      if (order.fulfillments.length > 0) {
        fullAddress = formatAddress(order.fulfillments[0]);
      }

      let petNamesStr = '';
      const petNames: string[] = [];
      if (order.products.length > 0) {
        order.products.forEach((item: any) => {
          const petName = item?.pet_plan?.pet_name;
          if (petName && !petNames.includes(petName)) {
            petNames.push(petName);
          }
        });

        // Create a deduplicated string of pet names
        petNamesStr = petNames.join(', ');
      }

      return {
        'Order ID': (
          <div className="flex items-center">
            <Link
              className="cursor-pointer underline text-anchor"
              to={order.id ? `order/${order.id}` : ''}
            >
              {order.id}
            </Link>
            {order.has_claim && (
              <span
                className="ml-1 text-red-600"
                title="There is a claim associated with this order"
              >
                ❗
              </span>
            )}
          </div>
        ),
        'Order Status': order.status,
        'Order Date': `${mapSntApiDate(orderDate)}`,
        'Order Type': order.order_type,
        'Order Total': `$${order.total_price}`,
        'Pet(s)': petNames.length > 0 ? petNamesStr : '--',
        'Payment Status': order.charge ? order.charge.status : '--',
        'Shipping Address': fullAddress,
      };
    });
};

export const CustomerOrders = ({ customer, refreshCustomer }: CustomerOrdersProps) => {
  let orders: any[] = [];
  orders = orders.concat(customer.orders.upcoming);
  orders = orders.concat(customer.orders.processing);
  orders = orders.concat(customer.orders.past);

  const hasPreviewOrders = orders.some((order) => order.status === OrderStatus.PREVIEW);
  const isCustomerCancelled = customer.status === AccountStatus.Cancelled;
  const isInPaymentError = orders.some((order) => order.status === OrderStatus.HOLD);
  const showRegenerateCTA =
    hasPreviewOrders && !isCustomerCancelled && !isInPaymentError && isAllModuleAccessUser();

  const [showOneTimeOrderModal, setShowOneTimeOrderModal] = useState(false);
  const [showRegeneratePreviewModal, setShowRegeneratePreviewModal] = useState(false);
  const [isRegeneratingPreview, setIsRegeneratingPreview] = useState(false);

  const handleRegeneratePreviewOrders = () => {
    setIsRegeneratingPreview(true);
    setShowRegeneratePreviewModal(false);
    axios
      .post(`accounts/${customer.id}/regenerate_preview_orders`)
      .then(() => {
        toast.success('Preview order(s) regenerated successfully.', BOTTOM_TOAST);
        refreshCustomer(() => setIsRegeneratingPreview(false));
      })
      .catch((e) => {
        toast.error(generateErrorMsg(e), BOTTOM_TOAST);
        setIsRegeneratingPreview(false);
      });
  };

  return (
    <div className="pt-3 pb-6">
      <h2 className="mb-1 flex items-center">
        <b className="inline-block">Orders</b>
        <div className="text-sm ml-2">
          <a
            onClick={() => setShowOneTimeOrderModal(true)}
            className="cursor-pointer underline text-anchor"
          >
            Create One-Time Order
          </a>
        </div>
        {showRegenerateCTA && (
          <div className="text-sm ml-2">
            <a
              onClick={() => setShowRegeneratePreviewModal(true)}
              className="cursor-pointer underline text-anchor"
            >
              Regenerate Preview Order(s)
            </a>
          </div>
        )}
      </h2>
      <div className="relative bg-white">
        {isRegeneratingPreview && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
            <InlineSpinner />
          </div>
        )}
        <Table
          header={CustomerOrdersHeader}
          data={mapOrders(orders)}
          currency={customer.default_currency}
        />
      </div>
      {showOneTimeOrderModal && (
        <OneTimeOrderModal
          customer={customer}
          onConfirm={() => {
            setShowOneTimeOrderModal(false);
            refreshCustomer();
          }}
          onCancel={() => setShowOneTimeOrderModal(false)}
        />
      )}
      {showRegeneratePreviewModal && (
        <ConfirmationModal
          title="Regenerate Preview Order(s)?"
          message="This will regenerate all preview orders for this customer. Are you sure you want to proceed?"
          confirmLabel="Confirm"
          onConfirm={handleRegeneratePreviewOrders}
          onCancel={() => setShowRegeneratePreviewModal(false)}
          isProcessLoading={isRegeneratingPreview}
        />
      )}
    </div>
  );
};
